import { useState } from 'react'
import { Info, Plus } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useComplianceItems, useComplianceMutations } from '../api'
import { COMPLIANCE_CATEGORY_LABEL, COMPLIANCE_FREQUENCY_LABEL } from '../types'
import type { ComplianceFrequency, ComplianceItemView } from '../types'

interface Draft {
  id?: string
  code: string
  name: string
  authority: string
  category: string
  frequency: string
  dueDayOfPeriod: string
  dueMonth: string
  leadDays: string
  sourceKind: string
  ownerEmployeeId: string
  instructions: string
  requiresEvidence: boolean
}

const EMPTY: Draft = {
  code: '', name: '', authority: '', category: 'FILING', frequency: 'ANNUAL',
  dueDayOfPeriod: '', dueMonth: '', leadDays: '', sourceKind: 'NONE', ownerEmployeeId: '',
  instructions: '', requiresEvidence: true,
}

/** What this company owes. Empty until somebody says. */
export function ComplianceItemsPage() {
  useBreadcrumb()
  const items = useComplianceItems()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  // Not useLookup('employees'): that endpoint is paged, and a lookup hook over a
  // PageResponse yields undefined and an empty picker with no error.
  const actions = useComplianceMutations()

  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)

  function act(body: () => Promise<unknown>, fallback: string, inDialog = false) {
    void (async () => {
      setError(null)
      setDialogError(null)
      try {
        await body()
      } catch (caught) {
        const detail = toApiProblem(caught)?.detail ?? fallback
        if (inDialog) setDialogError(detail)
        else setError(detail)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Obligations"
        subtitle="The statutory calendar's source list."
        actions={
          <Button type="button" onClick={() => { setDialogError(null); setDraft({ ...EMPTY }) }}>
            <Plus className="size-4" aria-hidden="true" />
            New obligation
          </Button>
        }
      />

      {error && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      <p className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Nothing is seeded. Which returns you owe, and when, comes from your licence and your
        jurisdiction — this product cannot know either, and a plausible shipped list would be a
        guess somebody eventually reports against. There is no delete either: the periods filed
        against an obligation are the audit trail, so it is deactivated instead.
      </p>

      <Card>
        {items.isPending && <Skeleton className="m-4 h-24" />}
        {items.isError && <ErrorState error={items.error} onRetry={() => void items.refetch()} />}
        {items.isSuccess && items.data.length === 0 && (
          <EmptyState
            title="No obligation is recorded"
            description="Add the returns, renewals and payments this company owes. Each one can then generate its periods for a year."
          />
        )}
        {items.isSuccess && items.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
              <tr>
                <th className="p-3 text-start font-medium">Code</th>
                <th className="p-3 text-start font-medium">Obligation</th>
                <th className="p-3 text-start font-medium">Owed to</th>
                <th className="p-3 text-start font-medium">How often</th>
                <th className="p-3 text-start font-medium">Owner</th>
                <th className="p-3 text-start font-medium">Open</th>
                <th className="p-3 text-start font-medium">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {items.data.map((item: ComplianceItemView) => (
                <tr key={item.id} className="border-b border-[var(--color-border)]">
                  <td className="p-3 font-mono">{item.code}</td>
                  <td className="p-3">
                    {item.name}
                    {item.requiresEvidence && (
                      <span className="ms-2 text-xs text-[var(--color-muted)]">
                        evidence required
                      </span>
                    )}
                  </td>
                  <td className="p-3">{item.authority ?? '—'}</td>
                  <td className="p-3">{COMPLIANCE_FREQUENCY_LABEL[item.frequency as ComplianceFrequency] ?? item.frequency}</td>
                  <td className="p-3">{item.ownerName ?? 'Nobody'}</td>
                  <td className="p-3">{item.openPeriods}</td>
                  <td className="p-3">
                    <StatusBadge
                      status={item.active ? 'ACTIVE' : 'INACTIVE'}
                      tone={item.active ? 'success' : 'neutral'}
                      label={item.active ? 'Active' : 'Deactivated'}
                    />
                  </td>
                  <td className="p-3 text-end">
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDialogError(null)
                          setDraft({
                            id: item.id,
                            code: item.code,
                            name: item.name,
                            authority: item.authority ?? '',
                            category: item.category,
                            frequency: item.frequency,
                            dueDayOfPeriod: item.dueDayOfPeriod?.toString() ?? '',
                            dueMonth: item.dueMonth?.toString() ?? '',
                            leadDays: item.leadDays?.toString() ?? '',
                            sourceKind: item.sourceKind,
                            ownerEmployeeId: item.ownerEmployeeId ?? '',
                            instructions: item.instructions ?? '',
                            requiresEvidence: item.requiresEvidence,
                          })
                        }}
                      >
                        Edit
                      </Button>
                      {item.active && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => act(
                            () => actions.deactivateItem.mutateAsync(item.id),
                            'That obligation could not be deactivated')}
                        >
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setDialogError(null) } }}
        title={draft?.id ? 'Edit obligation' : 'New obligation'}
        submitLabel="Save"
        loading={actions.createItem.isPending || actions.updateItem.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!draft) return
          const body = {
            legalEntityId: entities.data?.[0]?.id ?? null,
            code: draft.code,
            name: draft.name,
            authority: draft.authority || null,
            category: draft.category,
            frequency: draft.frequency,
            dueDayOfPeriod: draft.dueDayOfPeriod ? Number(draft.dueDayOfPeriod) : null,
            dueMonth: draft.dueMonth ? Number(draft.dueMonth) : null,
            leadDays: draft.leadDays ? Number(draft.leadDays) : null,
            sourceKind: draft.sourceKind,
            sourceRefId: null,
            ownerEmployeeId: draft.ownerEmployeeId || null,
            instructions: draft.instructions || null,
            requiresEvidence: draft.requiresEvidence,
            active: true,
          }
          act(async () => {
            if (draft.id) await actions.updateItem.mutateAsync({ id: draft.id, ...body })
            else await actions.createItem.mutateAsync(body)
            setDraft(null)
          }, 'That obligation could not be saved', true)
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="item-code">Code</Label>
              <Input
                id="item-code" value={draft.code}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="item-name">Name</Label>
              <Input
                id="item-name" value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="item-authority">Owed to</Label>
              <Input
                id="item-authority" value={draft.authority}
                placeholder="The ministry, the bank, the auditor"
                onChange={(event) => setDraft({ ...draft, authority: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="item-category">Category</Label>
              <Select
                id="item-category" className="w-full" value={draft.category}
                onChange={(event) => setDraft({ ...draft, category: event.target.value })}
              >
                {Object.entries(COMPLIANCE_CATEGORY_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="item-frequency">How often</Label>
              <Select
                id="item-frequency" className="w-full" value={draft.frequency}
                onChange={(event) => setDraft({ ...draft, frequency: event.target.value })}
              >
                {Object.entries(COMPLIANCE_FREQUENCY_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="item-day">Due on day</Label>
              <Input
                id="item-day" type="number" value={draft.dueDayOfPeriod}
                onChange={(event) => setDraft({ ...draft, dueDayOfPeriod: event.target.value })}
              />
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Leave empty and the period carries no due date, which the calendar says rather
                than showing a blank.
              </p>
            </div>
            {draft.frequency === 'ANNUAL' && (
              <div>
                <Label htmlFor="item-month">In month</Label>
                <Input
                  id="item-month" type="number" value={draft.dueMonth}
                  onChange={(event) => setDraft({ ...draft, dueMonth: event.target.value })}
                />
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  Required for an annual obligation: without it there is no date.
                </p>
              </div>
            )}
            {draft.frequency === 'ON_EVENT' && (
              <>
                <div>
                  <Label htmlFor="item-source">Raised by</Label>
                  <Select
                    id="item-source" className="w-full" value={draft.sourceKind}
                    onChange={(event) => setDraft({ ...draft, sourceKind: event.target.value })}
                  >
                    <option value="NONE">Nothing — pick one</option>
                    <option value="COMPANY_DOCUMENT">A company document's expiry</option>
                    <option value="EMPLOYEE_DOCUMENT">An employee document's expiry</option>
                    <option value="PRO_TEMPLATE">A PRO process</option>
                    <option value="WPS">A wage-protection run</option>
                    <option value="GPSSA">A pension filing</option>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="item-lead">Days before</Label>
                  <Input
                    id="item-lead" type="number" value={draft.leadDays}
                    onChange={(event) => setDraft({ ...draft, leadDays: event.target.value })}
                  />
                </div>
              </>
            )}
            <div>
              <Label htmlFor="item-owner">Owner</Label>
              <EmployeePicker
                  id="item-owner"
                  value={draft.ownerEmployeeId}
                  onChange={(employeeId) => setDraft({ ...draft, ownerEmployeeId: employeeId ?? '' })}
                />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  id="item-evidence" type="checkbox" className="size-4"
                  checked={draft.requiresEvidence}
                  onChange={(event) =>
                    setDraft({ ...draft, requiresEvidence: event.target.checked })}
                />
                Needs evidence before it can be marked done
              </label>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="item-instructions">How it is done</Label>
              <Textarea
                id="item-instructions" value={draft.instructions}
                onChange={(event) => setDraft({ ...draft, instructions: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
